import React, { useState, useEffect } from 'react';
import { api, formatINR, formatDate, statusClass } from '../utils/api';

const TABS = ['P&L', 'Overdue Ageing', 'Vendor Summary', 'TDS Summary'];

export default function Reports() {
  const [tab,     setTab]     = useState('P&L');
  const [pl,      setPl]      = useState(null);
  const [overdue, setOverdue] = useState(null);
  const [vendors, setVendors] = useState(null);
  const [tds,     setTds]     = useState(null);
  const [tdsFY,   setTdsFY]   = useState(null);
  const [expandedClient, setExpandedClient] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    if (tab === 'P&L') {
      api.get('/reports/pl').then(d => { setPl(d); setLoading(false); }).catch(() => setLoading(false));
    } else if (tab === 'Overdue Ageing') {
      api.get('/reports/overdue').then(d => { setOverdue(d); setLoading(false); }).catch(() => setLoading(false));
    } else if (tab === 'Vendor Summary') {
      api.get('/reports/vendors').then(d => { setVendors(d); setLoading(false); }).catch(() => setLoading(false));
    } else if (tab === 'TDS Summary') {
      const q = tdsFY ? `?fy=${tdsFY}` : '';
      api.get(`/reports/tds${q}`).then(d => { setTds(d); setTdsFY(d.fy); setLoading(false); }).catch(() => setLoading(false));
    }
  }, [tab, tdsFY]);

  return (
    <div className="page-content">
      <div className="topbar">
        <div className="topbar-title">Reports</div>
      </div>

      <div className="filter-tabs" style={{marginBottom: -4}}>
        {TABS.map(t => (
          <button key={t} className={`filter-tab ${tab===t?'active':''}`} onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>

      {loading && <div className="loading-state">Loading…</div>}

      {/* P&L Tab */}
      {tab === 'P&L' && pl && !loading && (
        <div className="card">
          <div className="card-header"><span className="card-title">Profit & Loss by Project</span></div>
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Project</th><th>Client</th><th>Status</th>
                <th className="num">Contract</th>
                <th className="num">Received</th>
                <th className="num">Outstanding</th>
                <th className="num">Vendor Cost</th>
                <th className="num">Margin</th>
                <th className="num">Margin %</th>
              </tr></thead>
              <tbody>
                {pl.rows.map(r => (
                  <tr key={r.id}>
                    <td style={{fontWeight:600,color:'var(--text-dark)'}}>{r.project_name}</td>
                    <td style={{fontSize:11,color:'var(--text-light)'}}>{r.client_name}</td>
                    <td><span className={`badge ${statusClass(r.status)}`}>{r.status}</span></td>
                    <td className="num">{formatINR(r.contract_value)}</td>
                    <td className="num" style={{color:'var(--success)',fontWeight:600}}>{formatINR(r.received)}</td>
                    <td className="num" style={{color:r.outstanding>0?'var(--danger)':'var(--success)'}}>{formatINR(r.outstanding)}</td>
                    <td className="num">{formatINR(r.vendor_cost)}</td>
                    <td className="num" style={{color:'var(--success)',fontWeight:700}}>{formatINR(r.margin)}</td>
                    <td className="num">
                      <span style={{fontWeight:700,color:r.margin_pct>=50?'var(--success)':r.margin_pct>=30?'var(--warning)':'var(--danger)'}}>
                        {r.margin_pct}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              {pl.totals && (
                <tfoot><tr>
                  <td colSpan={3}>Total</td>
                  <td className="num">{formatINR(pl.totals.contract_value)}</td>
                  <td className="num" style={{color:'var(--success)'}}>{formatINR(pl.totals.received)}</td>
                  <td className="num" style={{color:'var(--danger)'}}>{formatINR(pl.totals.outstanding)}</td>
                  <td className="num">{formatINR(pl.totals.vendor_cost)}</td>
                  <td className="num" style={{color:'var(--success)'}}>{formatINR(pl.totals.margin)}</td>
                  <td className="num" style={{color:'var(--success)'}}>{pl.totals.margin_pct}%</td>
                </tr></tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* Overdue Tab */}
      {tab === 'Overdue Ageing' && overdue && !loading && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12}}>
          {[
            { key:'under30', label:'Under 30 Days',  colorClass:'amber' },
            { key:'under60', label:'Under 60 Days',  colorClass:'orange' },
            { key:'over60',  label:'Over 60 Days',   colorClass:'red' },
          ].map(({ key, label, colorClass }) => (
            <div key={key} className="card">
              <div className={`card-header head-${colorClass}`} style={{background: colorClass==='amber'?'#fffbf2':colorClass==='orange'?'#fff8f4':'#fff5f4'}}>
                <span className={`card-title title-${colorClass}`} style={{color:colorClass==='amber'?'var(--warning)':colorClass==='orange'?'#b54a00':'var(--danger)'}}>{label}</span>
                <span style={{fontSize:12,fontWeight:700,color:colorClass==='amber'?'var(--warning)':colorClass==='orange'?'#b54a00':'var(--danger)'}}>
                  {formatINR(overdue.totals[key])}
                </span>
              </div>
              {overdue.buckets[key].length === 0 ? (
                <div className="empty-state" style={{padding:'20px'}}><div className="empty-state-icon">✓</div>No projects</div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Project</th><th>Client</th><th className="num">Outstanding</th><th className="num">Days</th></tr></thead>
                    <tbody>
                      {overdue.buckets[key].map(p => (
                        <tr key={p.id}>
                          <td style={{fontWeight:600,color:'var(--text-dark)',fontSize:11}}>{p.project_name}</td>
                          <td style={{fontSize:10,color:'var(--text-light)'}}>{p.client_name}</td>
                          <td className="num" style={{fontWeight:700,color:'var(--danger)'}}>{formatINR(p.outstanding)}</td>
                          <td className="num" style={{fontSize:11,color:'var(--text-light)'}}>{p.days_overdue}d</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Vendor Summary Tab */}
      {tab === 'Vendor Summary' && vendors && !loading && (
        <div className="card">
          <div className="card-header"><span className="card-title">Vendor Payment Summary</span></div>
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Vendor</th><th>Service Type</th>
                <th className="num">Projects</th>
                <th className="num">Total Budgeted</th>
                <th className="num">Total Paid</th>
                <th className="num">Outstanding</th>
              </tr></thead>
              <tbody>
                {vendors.rows.map(v => (
                  <tr key={v.id}>
                    <td style={{fontWeight:600,color:'var(--text-dark)'}}>{v.vendor_name}</td>
                    <td style={{fontSize:11,color:'var(--text-light)'}}>{v.service_type||'—'}</td>
                    <td className="num">{v.project_count}</td>
                    <td className="num">{formatINR(v.total_budgeted)}</td>
                    <td className="num" style={{color:'var(--success)',fontWeight:600}}>{formatINR(v.total_paid)}</td>
                    <td className="num" style={{color:v.total_outstanding>0?'var(--danger)':'var(--success)',fontWeight:700}}>{formatINR(v.total_outstanding)}</td>
                  </tr>
                ))}
              </tbody>
              {vendors.totals && (
                <tfoot><tr>
                  <td colSpan={3}>Total</td>
                  <td className="num">{formatINR(vendors.totals.total_budgeted)}</td>
                  <td className="num" style={{color:'var(--success)'}}>{formatINR(vendors.totals.total_paid)}</td>
                  <td className="num" style={{color:'var(--danger)'}}>{formatINR(vendors.totals.total_outstanding)}</td>
                </tr></tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* TDS Summary Tab */}
      {tab === 'TDS Summary' && tds && !loading && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">TDS Deducted by Client</span>
            {tds.availableFYs && tds.availableFYs.length > 0 && (
              <select
                value={tdsFY || tds.fy}
                onChange={e => setTdsFY(e.target.value)}
                style={{fontSize:12,padding:'4px 8px',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)'}}
              >
                {tds.availableFYs.map(fy => <option key={fy} value={fy}>FY {fy}</option>)}
              </select>
            )}
          </div>

          {tds.rows.length === 0 ? (
            <div className="empty-state" style={{padding:'20px'}}><div className="empty-state-icon">—</div>No TDS recorded for this year</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr>
                  <th>Client</th>
                  <th className="num">Project TDS</th>
                  <th className="num">Retainer TDS</th>
                  <th className="num">Total TDS</th>
                  <th className="num">Entries</th>
                </tr></thead>
                <tbody>
                  {tds.rows.map(r => (
                    <React.Fragment key={r.client_id}>
                      <tr
                        onClick={() => setExpandedClient(expandedClient === r.client_id ? null : r.client_id)}
                        style={{cursor:'pointer'}}
                      >
                        <td style={{fontWeight:600,color:'var(--text-dark)'}}>
                          {expandedClient === r.client_id ? '▾ ' : '▸ '}{r.client_name}
                        </td>
                        <td className="num">{formatINR(r.project_tds)}</td>
                        <td className="num">{formatINR(r.retainer_tds)}</td>
                        <td className="num" style={{fontWeight:700,color:'var(--text-dark)'}}>{formatINR(r.total_tds)}</td>
                        <td className="num" style={{color:'var(--text-light)'}}>{r.entry_count}</td>
                      </tr>
                      {expandedClient === r.client_id && (
                        <tr>
                          <td colSpan={5} style={{background:'var(--bg-light)',padding:0}}>
                            <table style={{width:'100%'}}>
                              <thead><tr>
                                <th style={{fontSize:10}}>Date</th>
                                <th style={{fontSize:10}}>Source</th>
                                <th style={{fontSize:10}}>Reference</th>
                                <th className="num" style={{fontSize:10}}>TDS Amount</th>
                              </tr></thead>
                              <tbody>
                                {tds.entries.filter(e => e.client_id === r.client_id).map((e, i) => (
                                  <tr key={i}>
                                    <td style={{fontSize:11}}>{formatDate(e.tds_date)}</td>
                                    <td style={{fontSize:11,color:'var(--text-light)'}}>{e.source_type} — {e.source_name}</td>
                                    <td style={{fontSize:11,color:'var(--text-light)'}}>{e.reference || '—'}</td>
                                    <td className="num" style={{fontSize:11}}>{formatINR(e.tds_amount)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot><tr>
                  <td>Total</td>
                  <td className="num">{formatINR(tds.rows.reduce((s,r)=>s+r.project_tds,0))}</td>
                  <td className="num">{formatINR(tds.rows.reduce((s,r)=>s+r.retainer_tds,0))}</td>
                  <td className="num">{formatINR(tds.grandTotal)}</td>
                  <td className="num">{tds.entries.length}</td>
                </tr></tfoot>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
